import {
  type DrawDefinition,
  EventTypeEnum,
  type PositionAssignment,
  StageTypeEnum,
  type Tournament,
} from '@clubspark-node/clubspark-tennis-types';
import { orderEvents } from '@clubspark-react/data-fns';
import { shallowEqual } from 'fast-equals';
import { atom, type Atom } from 'jotai';
import { atomFamily, atomWithDefault, selectAtom } from 'jotai/utils';
import { entryStatusConstants, participantTypes, policyConstants, tournamentEngine } from 'tods-competition-factory';

import { avoidanceConstants } from 'constants/avoidance';
import { entryPositionSort, entryStatusSort } from 'containers/eventsSplitDraws/entries/utils';
import { getEventsDivisionData } from 'containers/matchUps/utils';
import { tournamentParticipantsAtom } from 'hooks/tournament/useTournamentParticipants';
import { type EventInterface, type FlightProfile, type ParticipantInterface } from 'typedefs/store/tmxTypes';
import { getDirectiveFromPolicy, getRuleFromPolicy } from 'utils/avoidanceUtils';
import { factorySafeTimeToDayjs } from 'utils/dateTimeUtils';
import { getParticipantMapperFor, participantTypeIndividualFilter } from 'utils/participant';

import { tournamentAtom, tournamentsAtom } from './tournament';
import { getIsDrawStageGenerated, getIsParticipantAssignedToDraw } from './utils';

const { POLICY_TYPE_AVOIDANCE } = policyConstants;
const { STRUCTURE_SELECTED_STATUSES, ALTERNATE, UNPAIRED, UNGROUPED, WITHDRAWN } = entryStatusConstants;
const { TEAM } = participantTypes;

const hydrateEvents = (tournament: Tournament): EventInterface[] => {
  const hydratedEvents = (tournament.events || []).map((event) => {
    return {
      tournamentId: tournament.tournamentId,
      ...event,
      startDate: factorySafeTimeToDayjs(event.startDate || tournament.startDate, tournament.localTimeZone).toDate(),
      endDate: factorySafeTimeToDayjs(event.endDate || tournament.startDate, tournament.localTimeZone).toDate(),
      tournamentCategory: tournament.extensions?.find((extension) => extension.name === 'level')?.value?.level
        ?.category,
    };
  });
  const hydratedEventsMap = Object.fromEntries(
    Object.entries(hydratedEvents).map(([, event]) => [event.eventId, event]),
  );
  const eventsDivisionData = getEventsDivisionData(hydratedEvents);
  const orderedEvents = orderEvents(eventsDivisionData);
  return orderedEvents.map(({ eventId }) => hydratedEventsMap[eventId]);
};

export const eventsAtom = selectAtom(tournamentAtom, hydrateEvents, shallowEqual);

export const teamEventsAtom = selectAtom(
  eventsAtom,
  (events) =>
    events
      .filter((event) => event?.eventType === EventTypeEnum.TEAM)
      .map(({ eventId, eventName }) => ({ eventId, eventName })),
  shallowEqual,
);

export const allEventsAtom = selectAtom(
  tournamentsAtom,
  (tournaments) => Object.values(tournaments).filter(Boolean).flatMap(hydrateEvents),
  shallowEqual,
);

export const eventsMapAtom = selectAtom(
  allEventsAtom,
  (events) => Object.fromEntries(Object.entries(events).map(([, event]) => [event.eventId, event])),
  shallowEqual,
);

export const eventAtomFamily = atomFamily((eventId: string) =>
  selectAtom(eventsMapAtom, (eventsMap) => eventsMap[eventId]),
);

export const eventTournamentIdAtomFamily = atomFamily((eventId: string) =>
  selectAtom(eventAtomFamily(eventId), (event) => event?.tournamentId),
);

export const eventTypeAtomFamily = atomFamily((eventId: string) =>
  selectAtom(eventAtomFamily(eventId), (event) => event?.eventType),
);

export const drawDefinitionsAtomFamily = atomFamily(
  (eventId: string): Atom<DrawDefinition[]> => selectAtom(eventAtomFamily(eventId), (event) => event?.drawDefinitions),
);

export const drawGeneratedAtomFamily = atomFamily((eventId: string) =>
  atom((get) => !!get(drawDefinitionsAtomFamily(eventId))?.filter((draw) => !!draw?.structures?.length)?.length),
);

export const mainDrawGeneratedAtomFamily = atomFamily(
  (eventId: string): Atom<boolean> =>
    selectAtom(drawDefinitionsAtomFamily(eventId), (drawDefinitions) =>
      getIsDrawStageGenerated(drawDefinitions, StageTypeEnum.MAIN),
    ),
);

export const qualifyingEnabledAtomFamily = atomFamily((eventId: string) =>
  atom((get) => {
    const event = get(eventAtomFamily(eventId));
    return event?.extensions?.find((extension) => extension.name === 'eventProfile')?.value?.qualifying;
  }),
);

export const qualifyingDrawGeneratedAtomFamily = atomFamily(
  (eventId: string): Atom<boolean> =>
    selectAtom(drawDefinitionsAtomFamily(eventId), (drawDefinitions) =>
      getIsDrawStageGenerated(drawDefinitions, StageTypeEnum.QUALIFYING),
    ),
);

export const drawAtomFamily = atomFamily(
  ({ eventId, drawId }: { eventId: string; drawId: string }) =>
    atom((get) => {
      const drawDefinitions = get(drawDefinitionsAtomFamily(eventId));
      const flights = get(flightsAtomFamily(eventId));
      return (
        flights?.find((flight) => flight.drawId === drawId) ??
        drawDefinitions?.find((drawDefinition) => drawDefinition.drawId === drawId)
      );
    }),
  shallowEqual,
);

export const flightProfileAtomFamily = atomFamily(
  (eventId: string): Atom<FlightProfile> =>
    selectAtom(
      eventAtomFamily(eventId),
      (event) =>
        tournamentEngine.getFlightProfile({
          eventId: event?.eventId,
        })?.flightProfile,
    ),
);

export const policyDefinitionsAtomFamily = atomFamily(
  (eventId: string): Atom<any> =>
    selectAtom(
      eventAtomFamily(eventId),
      (event) =>
        event &&
        tournamentEngine.getPolicyDefinitions({
          eventId,
          policyTypes: [POLICY_TYPE_AVOIDANCE],
        })?.policyDefinitions,
    ),
);

export const avoidancePolicyRulesGroupsAtomFamily = atomFamily((eventId: string) =>
  atom((get) => {
    const policyDefinitions = get(policyDefinitionsAtomFamily(eventId));
    const tournamentParticipants = get(tournamentParticipantsAtom);

    const rules = [
      ...new Set([
        ...(policyDefinitions?.avoidance?.policyAttributes
          ?.filter((attribute) => getRuleFromPolicy(attribute.key, attribute.significantCharacters))
          ?.map((attribute) => getRuleFromPolicy(attribute.key, attribute.significantCharacters)) ?? []),
        ...(policyDefinitions?.avoidance?.policyAttributes
          ?.filter((attribute) => getDirectiveFromPolicy(attribute.directive))
          ?.map((attribute) => getDirectiveFromPolicy(attribute.directive)) ?? []),
      ]),
    ];
    const groupParticipantsFromAvoidance = policyDefinitions?.avoidance?.policyAttributes?.find(
      (attribute) => attribute.directive === avoidanceConstants.MAPPED_PARTICIPANT_GROUP,
    );
    const eventGroupParticipantsIds = groupParticipantsFromAvoidance?.includeIds;
    const groups = tournamentParticipants?.filter(
      (participant) =>
        eventGroupParticipantsIds &&
        participant.participantType === 'GROUP' &&
        eventGroupParticipantsIds.includes(participant.participantId),
    );
    return { rules, groups };
  }),
);

export const flightsAtomFamily = atomFamily((eventId: string) =>
  selectAtom(flightProfileAtomFamily(eventId), (flightProfile) => {
    const flights = flightProfile?.flights ?? [];
    return (
      [...flights]?.sort((flightA, flightB) => Number(flightA?.flightNumber) - Number(flightB?.flightNumber)) ?? []
    );
  }),
);

export const firstFlightDrawIdAtomFamily = atomFamily((eventId: string) =>
  atom((get) => {
    const flights = get(flightsAtomFamily(eventId));
    return flights[0]?.drawId;
  }),
);

export const structureEntriesAtomFamily = atomFamily((eventId: string) =>
  selectAtom(eventAtomFamily(eventId), (event) =>
    event?.entries?.filter((entry) => STRUCTURE_SELECTED_STATUSES?.includes(entry.entryStatus)),
  ),
);

export const eventEntriesAtomFamily = atomFamily((eventId: string) =>
  selectAtom(eventAtomFamily(eventId), (event) => event?.entries ?? []),
);

export const eventEntriesUngroupedAtomFamily = atomFamily((eventId: string) =>
  selectAtom(eventEntriesAtomFamily(eventId), (entries) => entries.filter((entry) => entry.entryStatus === UNGROUPED)),
);

export const eventEntriesUnpairedAtomFamily = atomFamily((eventId: string) =>
  selectAtom(eventEntriesAtomFamily(eventId), (entries) =>
    entries.filter((entry) => entry.entryStatus === UNPAIRED || entry.entryStatus === UNGROUPED),
  ),
);

export const eventEntriesActiveAtomFamily = atomFamily((eventId: string) =>
  selectAtom(eventEntriesAtomFamily(eventId), (entries) =>
    entries.filter(
      ({ entryStatus }) =>
        entryStatus === ALTERNATE || entryStatusConstants.DIRECT_ENTRY_STATUSES.includes(entryStatus),
    ),
  ),
);

export const eventEntriesWithdrawnAtomFamily = atomFamily((eventId: string) =>
  selectAtom(eventEntriesAtomFamily(eventId), (entries) =>
    entries.filter(({ entryStatus }) => entryStatus === WITHDRAWN),
  ),
);

export const eventParticipantsAtomFamily = atomFamily((eventId: string) =>
  atom((get) => {
    const entries = get(eventEntriesAtomFamily(eventId));
    const tournamentParticipants = get(tournamentParticipantsAtom);

    return entries.map(getParticipantMapperFor(tournamentParticipants)).filter((p) => p?.participantId);
  }),
);

export const eventMainDrawParticipantsAtomFamily = atomFamily((eventId: string) =>
  atom((get) => {
    const entries = get(eventEntriesAtomFamily(eventId));
    const eventType = get(eventTypeAtomFamily(eventId));
    const eventParticipants = get(eventParticipantsAtomFamily(eventId));
    const eventFlights = get(flightsAtomFamily(eventId));
    const isTeam = eventType === EventTypeEnum.TEAM;

    const mainDrawParticipants = entries
      ?.filter(
        (entry) =>
          (entryStatusConstants.DIRECT_ENTRY_STATUSES.includes(entry.entryStatus) &&
            entry.entryStage !== StageTypeEnum.QUALIFYING) ||
          getIsParticipantAssignedToDraw(entry.participantId, eventFlights, StageTypeEnum.MAIN),
      )
      .sort(entryPositionSort)
      .filter((assignment) => !!assignment?.participantId)
      .sort(entryStatusSort(entries))
      .map(getParticipantMapperFor(eventParticipants))
      .filter((participant) => !!participant?.participantId);

    return mainDrawParticipants.filter((entry) => (isTeam ? entry?.participantType === TEAM : entry));
  }),
);

export const eventAlternateDrawParticipantsAtomFamily = atomFamily((eventId: string) =>
  atomWithDefault((get) => {
    const entries = get(eventEntriesAtomFamily(eventId));
    const eventParticipants = get(eventParticipantsAtomFamily(eventId));
    const mainDrawParticipants = get(eventMainDrawParticipantsAtomFamily(eventId));

    return entries
      .filter((entry) => entry.entryStatus === ALTERNATE)
      ?.filter(
        ({ participantId }) => !mainDrawParticipants.some((participant) => participant.participantId === participantId),
      )
      .map(getParticipantMapperFor(eventParticipants))
      .filter((participant) => !!participant?.participantId);
  }),
);

export const eventWithdrawnDrawParticipantsAtomFamily = atomFamily((eventId: string) =>
  atom((get) => {
    const eventType = get(eventTypeAtomFamily(eventId));
    const withdrawnEntries = get(eventEntriesWithdrawnAtomFamily(eventId));
    const eventParticipants = get(eventParticipantsAtomFamily(eventId));

    const isTeam = eventType === EventTypeEnum.TEAM;

    return [...withdrawnEntries]
      ?.sort(entryPositionSort)
      ?.map(getParticipantMapperFor(eventParticipants))
      ?.filter((entry) => (isTeam ? entry?.participantType === TEAM : entry));
  }),
);

export const eventMainTeamParticipantsAtomFamily = atomFamily((eventId: string) =>
  atom((get) => {
    const eventParticipants = get(eventParticipantsAtomFamily(eventId));
    const ungroupedEntries = get(eventEntriesUngroupedAtomFamily(eventId));
    const availableTeamPlayerIdsAndPositions = ungroupedEntries.filter(
      (entry) => entry?.extensions?.find((extension) => extension?.name === 'statusDetail')?.value === 'available',
    );

    return [...availableTeamPlayerIdsAndPositions]
      ?.sort(entryPositionSort)
      ?.map(getParticipantMapperFor(eventParticipants))
      ?.filter(participantTypeIndividualFilter);
  }),
);

export const eventAlternateTeamParticipantsAtomFamily = atomFamily((eventId: string) =>
  atomWithDefault((get) => {
    const eventParticipants = get(eventParticipantsAtomFamily(eventId));
    const ungroupedEntries = get(eventEntriesUngroupedAtomFamily(eventId));
    const alternateTeamPlayerIdsAndPositions = ungroupedEntries.filter(
      (entry) => entry?.extensions?.find((extension) => extension?.name === 'statusDetail')?.value !== 'available',
    );

    return [...alternateTeamPlayerIdsAndPositions]
      ?.sort(entryPositionSort)
      ?.map(getParticipantMapperFor(eventParticipants))
      ?.filter(participantTypeIndividualFilter);
  }),
);

export const eventWithdrawnTeamParticipantsAtomFamily = atomFamily((eventId: string) =>
  atom((get) => {
    const eventParticipants = get(eventParticipantsAtomFamily(eventId));
    const withdrawnEntries = get(eventEntriesWithdrawnAtomFamily(eventId));

    return [...withdrawnEntries]
      ?.sort(entryPositionSort)
      ?.map(getParticipantMapperFor(eventParticipants))
      ?.filter(participantTypeIndividualFilter);
  }),
);

export const qualifyingDrawParticipantsAtomFamily = atomFamily((eventId: string) =>
  atomWithDefault((get) => {
    const eventEntries = get(eventEntriesAtomFamily(eventId));
    const eventParticipants = get(eventParticipantsAtomFamily(eventId));

    return eventEntries
      .filter(
        (entry) =>
          entryStatusConstants.DIRECT_ENTRY_STATUSES.includes(entry.entryStatus) &&
          entry.entryStage === StageTypeEnum.QUALIFYING,
      )
      .map(getParticipantMapperFor(eventParticipants));
  }),
);

export const eventPlacedParticipantsAtomFamily = atomFamily(
  ({
    eventId,
    structureStage,
  }: {
    eventId: string;
    structureStage: StageTypeEnum.MAIN | StageTypeEnum.QUALIFYING;
  }): Atom<ParticipantInterface[]> =>
    atom<ParticipantInterface[]>((get) => {
      const drawDefinitions = get(drawDefinitionsAtomFamily(eventId))?.filter((draw) => !!draw?.structures?.length);
      const tournamentParticipants = get(tournamentParticipantsAtom);
      const allEventDrawsPositionAssignments =
        drawDefinitions?.flatMap<PositionAssignment>(({ drawId, structures }) => {
          const structure = structures.find(
            (structure) => structure.stage === structureStage && structure.stageSequence === 1,
          );
          const structureId = structure?.structureId;
          return structureId
            ? tournamentEngine.getPositionAssignments({
                drawId,
                structureId,
              })?.positionAssignments
            : [];
        }) ?? [];

      return allEventDrawsPositionAssignments
        .filter((assignment) => !!assignment?.participantId)
        .map((positionAssignment) =>
          tournamentParticipants.find((participant) => participant.participantId === positionAssignment.participantId),
        );
    }),
);

export const qualifyingPlacedParticipantsIdsAtomFamily = atomFamily(
  (eventId: string): Atom<string[]> =>
    selectAtom(
      eventPlacedParticipantsAtomFamily({ eventId, structureStage: StageTypeEnum.QUALIFYING }),
      (participants) => participants?.map((participant) => participant.participantId),
    ),
);

export const eventPairedDrawParticipantsAtomFamily = atomFamily((eventId: string) =>
  atom((get) => {
    const eventParticipants = get(eventParticipantsAtomFamily(eventId));
    const activeEntries = get(eventEntriesActiveAtomFamily(eventId));

    return [...activeEntries]?.sort(entryPositionSort).reduce((pairs, entry) => {
      const participant = eventParticipants.find((participant) => participant.participantId === entry.participantId);
      return participant?.individualParticipants
        ? [
            {
              participantId: participant?.participantId,
              pair: participant.individualParticipants.map(getParticipantMapperFor(eventParticipants)).filter(Boolean),
            },
          ]
        : [];
    }, []);
  }),
);

export const eventPairedIndividualParticipantsAtomFamily = atomFamily((eventId: string) =>
  atom((get) => {
    const eventParticipants = get(eventParticipantsAtomFamily(eventId));
    const activeEntries = get(eventEntriesActiveAtomFamily(eventId));

    return [...activeEntries]?.sort(entryPositionSort)?.reduce((individuals, entry) => {
      const participant = getParticipantMapperFor(eventParticipants)(entry);
      participant?.individualParticipants?.forEach((p) => {
        if (!individuals.some((existing) => existing.participantId === p.participantId)) individuals.push(p);
      });
      return individuals;
    }, []);
  }),
);

export const eventUnpairedDrawParticipantsAtomFamily = atomFamily((eventId: string) =>
  atom((get) => {
    const eventParticipants = get(eventParticipantsAtomFamily(eventId));
    const unpairedEntries = get(eventEntriesUnpairedAtomFamily(eventId));

    return [...unpairedEntries]?.sort(entryPositionSort)?.map(getParticipantMapperFor(eventParticipants));
  }),
);
